import { Close } from "@mui/icons-material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import React, { useState } from "react";
import i18next from "../../i18n/i18n";
import { Attachment } from "./FileAttachments";
import { useSelector } from "react-redux";
import { selectFileAttachments, RootState } from "../../Store";
import { FilePreviewDialog } from "./FilePreviewDialog";

function Spinner({ size = 32 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 40 40" style={{ display: "block" }} aria-label="Loading">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#6a6ee6" strokeWidth="4" strokeDasharray="80" strokeDashoffset="60" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" dur="0.8s" from="0 20 20" to="360 20 20" repeatCount="indefinite" />
            </circle>
        </svg>
    );
}

export function FileAttachmentPreviewRow({ onRemove, answerGenerationActive }: { onRemove: (id: string) => void; answerGenerationActive: boolean }) {
    const [loaded, setLoaded] = useState<{ [id: string]: boolean }>({});
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);

    const handleImageLoad = (id: string) => setLoaded(prev => ({ ...prev, [id]: true }));

    const attachments: Array<Attachment> = useSelector(selectFileAttachments) || [];

    if (!attachments.length || answerGenerationActive) return null;
    return (
        <React.Fragment>
            <div
                draggable={false}
                style={{
                    display: "flex",
                    gap: 16,
                    margin: "4px 4px 0px 4px",
                    paddingLeft: 4,
                    paddingRight: 4,
                    minHeight: 50,
                    maxWidth: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "thin"
                }}
            >
                {attachments.map((att, index) => (
                    <div
                        key={att.id}
                        draggable={false}
                        onClick={e => {
                            e.stopPropagation();
                            setPreviewIndex(index);
                            setPreviewDialogOpen(true);
                        }}
                        style={{
                            position: "relative",
                            width: 50,
                            height: 50,
                            background: "#f8f9fa",
                            borderRadius: 12,
                            boxShadow: "0 1px 5px #0001",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            cursor: "pointer"
                        }}
                    >
                        {att.preview ? (
                            <>
                                {!loaded[att.id] && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 2,
                                            background: "rgba(255,255,255,0.6)",
                                            borderRadius: 12
                                        }}
                                    >
                                        <Spinner size={32} />
                                    </div>
                                )}
                                <img
                                    src={att.preview}
                                    draggable={false}
                                    alt={att.file.name}
                                    onLoad={() => handleImageLoad(att.id)}
                                    style={{
                                        width: 50,
                                        height: 50,
                                        objectFit: "cover",
                                        borderRadius: 7,
                                        display: loaded[att.id] ? "block" : "none"
                                    }}
                                />
                            </>
                        ) : (
                            <div>
                                <InsertDriveFileIcon style={{ fontSize: 38, color: "#6a6ee6" }} />
                                <span
                                    style={{
                                        display: "block",
                                        fontSize: 11,
                                        color: "#555",
                                        width: 50,
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        margin: "0 auto 4px auto",
                                        padding: "0 2px"
                                    }}
                                    title={att.file.name}
                                >
                                    {att.file.name}
                                </span>
                            </div>
                        )}
                        <button
                            aria-label={i18next.t("chat.removeAttachedFile")}
                            title={i18next.t("chat.removeAttachedFile")}
                            onClick={e => {
                                e.stopPropagation();
                                onRemove(att.id);
                            }}
                            style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                background: "#fff",
                                border: "none",
                                borderRadius: "50%",
                                boxShadow: "0 1px 5px #0002",
                                padding: 1,
                                cursor: "pointer",
                                zIndex: 3,
                                lineHeight: 1
                            }}
                            tabIndex={0}
                        >
                            <Close fontSize="small" style={{ color: "#b71c1c", fontSize: "10px" }} />
                        </button>
                    </div>
                ))}
            </div>
            {previewDialogOpen && (
                <FilePreviewDialog
                    files={attachments}
                    initialIndex={previewIndex}
                    open={previewDialogOpen}
                    onClose={() => {
                        setPreviewIndex(0);
                        setPreviewDialogOpen(false);
                    }}
                />
            )}
        </React.Fragment>
    );
}
